/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Boton,
  Titulo,
  EstadoCertificado,
  accessAPI,
  Sidebar
} from "../../../shared/index.js";
import { usePermiso } from "../../../shared";
import PropuestaHistorica from "./PropuestaHistorica.js";
import TarjetaDetallesProceso from "./TarjetaDetallesProceso.js";
import TarjetaSitiosAdicionales from "./TarjetaSitiosAdicionales.js";
import TarjetaNotas from "./TarjetaNotas.js";
import TarjetaJuguetes from "./TarjetaJuguetes.js";
import TarjetaReportesEnsayo from "./TarjetaReportesEnsayo.js";
import TarjetaArchivos from "./TarjetaArchivos.js";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { ConfirmDialog } from "primereact/confirmdialog";
import { formatFecha } from "../../../shared/index.js";

/**
 * Sección "Detalles" del proceso. Renderiza todas las cards excepto productos
 * (que ahora vive en su propia subruta). También aloja temporalmente los
 * botones provisorios de Solicitud y Propuesta comercial hasta decidir
 * dónde reubicarlos.
 */
export default function ProcesoDetalles() {
  const { puedeVer: puedeVerFacturacion, puedeEditar: puedeCrearPropuestas } =
    usePermiso("admin.facturacion");
  const { proceso, setProceso, loading } = useOutletContext();
  const toast = useRef(null);
  const navigate = useNavigate();

  const [testReports, setTestReports] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [procesoActivo, setProcesoActivo] = useState(proceso?.activo ?? null);
  const [loaderActivo, setLoaderActivo] = useState(false);
  const [historicaVisible, setHistoricaVisible] = useState(false);

  useEffect(() => {
    if (!proceso) return;
    let reportesEnsayo = [];
    if (proceso.actividad?.length) {
      for (const actividad of proceso.actividad) {
        if (actividad.reporteensayo?.length) {
          for (const reporteEnsayo of actividad.reporteensayo) {
            reportesEnsayo.push({ ...reporteEnsayo, actividad });
          }
        }
      }
    }
    setTestReports(reportesEnsayo);
    setArchivos(proceso.archivos || []);
    setProcesoActivo(proceso.activo);
  }, [proceso]);

  const getCertificadoEstado = () => {
    const certificado = proceso?.certificado?.[0];
    if (!certificado) return { pendiente: true };
    if (certificado.estadocertificado.vigentebool) {
      return { vigente: true, fechaEmision: certificado.fechaemision };
    } else if (certificado.estadocertificado.bajadefinitiva) {
      return { anulado: true };
    }
    return { suspendido: true };
  };

  function toggleProcesoActivo() {
    setLoaderActivo(true);
    accessAPI(
      "PUT",
      `admin/proceso/activo/${proceso.id}`,
      null,
      (respuesta) => {
        setLoaderActivo(false);
        setProcesoActivo(respuesta.activo);
        setProceso((prev) => ({ ...prev, activo: respuesta.activo }));
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.msg
        });
      },
      (error) => {
        setLoaderActivo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al cambiar estado del proceso",
          sticky: true
        });
      }
    );
  }

  function agregarTestReportAProceso(respuesta, esEdicion = false) {
    if (esEdicion) {
      setTestReports((prev) =>
        prev.map((r) => (r.id === respuesta.id ? { ...r, ...respuesta } : r))
      );
    } else {
      setTestReports((prev) => [...prev, respuesta]);
      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Reporte de ensayo agregado exitosamente"
      });
    }
  }

  function eliminarTestReportDeProceso(reporteId) {
    setTestReports((prev) => prev.filter((r) => r.id !== reporteId));
  }

  // Solicitud, propuesta comercial y sábana asociadas al proceso. Tanto la
  // propuesta como la sábana se vinculan EXCLUSIVAMENTE por procesoIds
  // (proceso.propuestaVinculada / proceso.sabanaVinculada), no a través de las
  // actividades.
  const solicitud = proceso?.solicitudproceso;
  const propuesta = proceso?.propuestaVinculada;
  const sabana = proceso?.sabanaVinculada;
  const tieneAntecedentes = solicitud || propuesta || sabana;

  const propuestaEstado = propuesta
    ? propuesta.fechaaceptacion
      ? {
          texto: `Aceptada el ${formatFecha(propuesta.fechaaceptacion)}`,
          color: "var(--verde)"
        }
      : propuesta.fechaemision
        ? {
            texto: `Pendiente de aceptación · emitida el ${formatFecha(propuesta.fechaemision)}`,
            color: "var(--amarillo)"
          }
        : {
            texto: "Sin emitir",
            color: "var(--grisTextos)"
          }
    : null;

  if (loading || !proceso) {
    return (
      <div>
        <Skeleton
          width="20rem"
          height="2rem"
          css={{ marginTop: "2em", marginBottom: "1em" }}
        />
        <div className="grid">
          <div
            className="col-12 lg:col-6"
            css={{ display: "flex", flexDirection: "column", gap: "1em" }}
          >
            <Skeleton width="100%" height="3rem" />
            <Card>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} css={{ marginBottom: "0.8em" }}>
                  <Skeleton
                    width="6rem"
                    height="0.9rem"
                    css={{ marginBottom: "0.3em" }}
                  />
                  <Skeleton width="14rem" height="1.1rem" />
                </div>
              ))}
            </Card>
            <Card>
              <Skeleton
                width="8rem"
                height="1.2rem"
                css={{ marginBottom: "1em" }}
              />
              <Skeleton width="100%" height="3rem" />
            </Card>
          </div>
          <div
            className="col-12 lg:col-6"
            css={{ display: "flex", flexDirection: "column", gap: "1em" }}
          >
            <Card>
              <Skeleton
                width="10rem"
                height="1.2rem"
                css={{ marginBottom: "1em" }}
              />
              <Skeleton width="100%" height="4rem" />
            </Card>
            <Card>
              <Skeleton
                width="8rem"
                height="1.2rem"
                css={{ marginBottom: "1em" }}
              />
              <Skeleton width="100%" height="2rem" />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {/* Sidebar para cargar una propuesta comercial histórica */}
      <Sidebar
        visible={historicaVisible}
        onHide={() => setHistoricaVisible(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        {historicaVisible && (
          <PropuestaHistorica
            proceso={proceso}
            onClose={() => setHistoricaVisible(false)}
            onCreada={(id) => {
              setHistoricaVisible(false);
              if (id) navigate(`/propuesta/${id}`);
            }}
          />
        )}
      </Sidebar>

      <Titulo
        urlVolver={`/clientes/${proceso.cliente.id}/procesos`}
        subtitulo={`${proceso.cliente.razonsocial} - ${proceso.nombrefantasia}`}
        etiqueta={procesoActivo !== 1 ? "Inactivo" : null}
        error={procesoActivo !== 1}
        botones={
          // Cargar una propuesta histórica crea una propuesta: pide full
          puedeCrearPropuestas && !propuesta && !sabana ? (
            <Boton azul callback={() => setHistoricaVisible(true)}>
              Agregar propuesta comercial
            </Boton>
          ) : null
        }
      >
        Proceso {proceso.nombre} - Detalles
      </Titulo>

      <div className="grid">
        <div
          className="col-12 lg:col-6"
          css={{ display: "flex", flexDirection: "column", gap: "1em" }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "1rem"
            }}
          >
            <div css={{ flex: 1 }}>
              <EstadoCertificado
                {...getCertificadoEstado()}
                sinRenovacion={proceso?.certificadotipo?.sinrenovacion}
              />
            </div>
            {(getCertificadoEstado().pendiente ||
              getCertificadoEstado().anulado) && (
              <Boton
                callback={toggleProcesoActivo}
                confirmacion="¿Está seguro?"
                loader={loaderActivo}
                rojo={procesoActivo === 1}
                verde={procesoActivo !== 1}
              >
                {procesoActivo === 1
                  ? "Desactivar proceso"
                  : "Activar proceso"}
              </Boton>
            )}
          </div>
          <TarjetaDetallesProceso
            proceso={proceso}
            onProcesoActualizado={(respuesta) =>
              setProceso((prev) => ({ ...prev, ...respuesta }))
            }
          />
          <TarjetaSitiosAdicionales
            proceso={proceso}
            onSitiosCambio={(respuesta) =>
              setProceso((prev) => ({ ...prev, ...respuesta }))
            }
          />
        </div>

        <div
          className="col-12 lg:col-6"
          css={{ display: "flex", flexDirection: "column", gap: "1em" }}
        >
          {tieneAntecedentes && (
            <Card>
              <Titulo>Antecedentes</Titulo>
              {solicitud && (
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1em"
                  }}
                >
                  <div>
                    <div css={{ fontWeight: 600 }}>Solicitud</div>
                    <div
                      css={{
                        color: "var(--grisTextos)",
                        fontSize: "0.85em"
                      }}
                    >
                      {solicitud.nombrefantasia}
                    </div>
                  </div>
                  <Boton
                    texto
                    uri={`/procesos/solicitudes/${solicitud.id}`}
                    nuevaPestana
                  >
                    Ver
                  </Boton>
                </div>
              )}
              {propuesta && (
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1em",
                    marginTop: solicitud ? "1em" : 0
                  }}
                >
                  <div>
                    <div css={{ fontWeight: 600 }}>Propuesta comercial</div>
                    <div
                      css={{
                        color: propuestaEstado.color,
                        fontSize: "0.85em",
                        fontWeight: 600
                      }}
                    >
                      {propuestaEstado.texto}
                    </div>
                  </div>
                  {/* Abrir la propuesta pide permiso de facturación */}
                  {puedeVerFacturacion && (
                    <Boton
                      texto
                      uri={`/propuesta/${propuesta.id}`}
                      nuevaPestana
                    >
                      Ver
                    </Boton>
                  )}
                </div>
              )}
              {sabana && (
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1em",
                    marginTop: solicitud || propuesta ? "1em" : 0
                  }}
                >
                  <div>
                    <div css={{ fontWeight: 600 }}>Propuesta sábana</div>
                    <div
                      css={{
                        color: sabana.fechaAceptacion
                          ? "var(--verde)"
                          : "var(--amarillo)",
                        fontSize: "0.85em",
                        fontWeight: 600
                      }}
                    >
                      {sabana.fechaAceptacion
                        ? `Aceptada el ${formatFecha(sabana.fechaAceptacion)}`
                        : "Pendiente de aceptación"}
                    </div>
                  </div>
                  {puedeVerFacturacion && (
                    <Boton
                      texto
                      uri={`/propuestasabana/${sabana.id}`}
                      nuevaPestana
                    >
                      Ver
                    </Boton>
                  )}
                </div>
              )}
            </Card>
          )}
          <TarjetaReportesEnsayo
            proceso={proceso}
            testReports={testReports}
            onReporteAgregado={agregarTestReportAProceso}
            onReporteEliminado={eliminarTestReportDeProceso}
          />
          <TarjetaNotas proceso={proceso} />
          {proceso.esjuguetes && (
            <TarjetaJuguetes proceso={proceso} toast={toast} />
          )}
          <TarjetaArchivos
            proceso={proceso}
            archivos={archivos}
            onArchivosChange={setArchivos}
          />
        </div>
      </div>
    </>
  );
}
